import { useState, useEffect } from "react";
import { useNavigation, useActionData } from "react-router-dom";

/**
 * Custom hook to manage button states for form submissions
 * @param {Object} options - Configuration options
 * @param {number} options.errorResetTime - Time in ms to reset from error state (default: 3000)
 * @param {number} options.successResetTime - Time in ms to reset from success state (default: 2000)
 * @returns {Object} - Button state and handler
 */
export function useButtonState(options = {}) {
  const { errorResetTime = 3000, successResetTime = 2000 } = options;
  const navigation = useNavigation();
  const data = useActionData();
  const [buttonState, setButtonState] = useState("default");

  // Handle button state based on navigation and action data
  useEffect(() => {
    if (navigation.state === "submitting") {
      setButtonState("success"); // Use success styling for loading
    } else if (navigation.state === "idle") {
      if (data && (Object.keys(data).length > 0 || (data.errors && Object.keys(data.errors).length > 0))) {
        // If there are errors, show error state
        setButtonState("error");
        // Reset to default after specified time
        const timer = setTimeout(() => setButtonState("default"), errorResetTime);
        return () => clearTimeout(timer);
      } else if (navigation.state === "idle" && buttonState === "success") {
        // If we were loading and now idle with no errors, keep success briefly
        const timer = setTimeout(() => setButtonState("default"), successResetTime);
        return () => clearTimeout(timer);
      }
    }
  }, [navigation.state, data, buttonState, errorResetTime, successResetTime]);

  const handleFormSubmit = () => {
    setButtonState("success"); // Use success styling during submit
  };

  return {
    buttonState,
    handleFormSubmit,
    isSubmitting: navigation.state === "submitting"
  };
} 